import { isDashboardUnlocked } from "../auth/DashboardLock";
import { useNavigate } from "react-router-dom";
import React, { useCallback, useEffect, useState } from "react";
import supabase from "../supabaseClient";
import TaskListSettingsModal from "../components/tasks/TaskListSettingsModal";

// Student components
import StudentList from "../components/students/StudentList";
import AddStudentModal from "../components/students/AddStudentModal";
import DeleteStudentModal from "../components/students/DeleteStudentModal";
import EditTaskModal from "../components/tasks/EditTaskModal";
import AddGroupModal from "../components/groups/AddGroupModal";
import EditGroupModal from "../components/groups/EditGroupModal";
import DeleteGroupModal from "../components/groups/DeleteGroupModal";

// Task components
import AddTaskModal from "../components/tasks/AddTaskModal";
import DeleteTaskModal from "../components/tasks/DeleteTaskModal";
import TaskFeedbackReportModal from "../components/tasks/TaskFeedbackReportModal";
import TaskIcon from "../components/TaskIcon";



export default function TeacherDashboard() {
  const navigate = useNavigate();

  const [students, setStudents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [groups, setGroups] = useState([]);
  const [editTask, setEditTask] = useState(null); // holds task being edited
  const [editGroup, setEditGroup] = useState(null);

  const [taskListTitle, setTaskListTitle] = useState("Taken van vandaag");
  const [tasksVisibleOnHome, setTasksVisibleOnHome] = useState(true);
  const [classTimerEnabled, setClassTimerEnabled] = useState(false);
  const [classTimerMinutes, setClassTimerMinutes] = useState(10);
  const [classTimerStartedAt, setClassTimerStartedAt] = useState(null);
  const [classTimerPausedAt, setClassTimerPausedAt] = useState(null);
  const [showTaskSettings, setShowTaskSettings] = useState(false);


  const [showAddStudent, setShowAddStudent] = useState(false);
  const [deleteStudentId, setDeleteStudentId] = useState(null);

  const [showAddTask, setShowAddTask] = useState(false);
  const [deleteTaskId, setDeleteTaskId] = useState(null);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [deleteGroupId, setDeleteGroupId] = useState(null);

  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskIcon, setNewTaskIcon] = useState("📘");
  const [newTaskPriority, setNewTaskPriority] = useState("required");
  const [newTaskSamenwerken, setNewTaskSamenwerken] = useState(false);
  const [newTaskZelfNakijken, setNewTaskZelfNakijken] = useState(false);
  const [newTaskFeedbackEnabled, setNewTaskFeedbackEnabled] = useState(false);
  const [newTaskAudience, setNewTaskAudience] = useState("all");
  const [newTaskGroupIds, setNewTaskGroupIds] = useState([]);

  const [editTaskAudience, setEditTaskAudience] = useState("all");
  const [editTaskGroupIds, setEditTaskGroupIds] = useState([]);
  const [taskAssignmentsByTaskId, setTaskAssignmentsByTaskId] = useState({});
  const [taskProgressByTaskId, setTaskProgressByTaskId] = useState({});

  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [feedbackReportTask, setFeedbackReportTask] = useState(null);

  // -------------------------------------------------------
  // 1️⃣ Protect the dashboard — must unlock via PIN first
  // -------------------------------------------------------
  useEffect(() => {
    if (!isDashboardUnlocked()) {
      console.log("Dashboard locked → redirecting to PIN screen");
      navigate("/pin", { replace: true });
    }
  }, [navigate]);


// Title of the dagtaken for that teacher
  useEffect(() => {
  async function loadTeacherSettings() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from("teachers")
      .select("task_list_title, tasks_visible_on_home, class_timer_enabled, class_timer_duration_minutes, class_timer_started_at, class_timer_paused_at")
      .eq("id", user.id)
      .single();

    if (data?.task_list_title) {
      setTaskListTitle(data.task_list_title);
    }

    if (data?.tasks_visible_on_home !== undefined && data?.tasks_visible_on_home !== null) {
      setTasksVisibleOnHome(data.tasks_visible_on_home);
    } else {
      setTasksVisibleOnHome(true);
    }

    setClassTimerEnabled(Boolean(data?.class_timer_enabled));
    setClassTimerMinutes(data?.class_timer_duration_minutes || 10);
    setClassTimerStartedAt(data?.class_timer_started_at || null);
    setClassTimerPausedAt(data?.class_timer_paused_at || null);
  }

  loadTeacherSettings();
}, []);

async function resetAllTaskStatuses() {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) return;

  // Delete all task_status entries belonging to this teacher
  const { error } = await supabase
    .from("task_status")
    .delete()
    .in(
      "task_id",
      tasks.map((t) => t.id)
    );

  if (error) {
    console.error("Error resetting task progress:", error);
    alert("Er ging iets mis.");
    return;
  }
  await loadTaskProgress();
}



  // -------------------------------------------------------
  // 2️⃣ FIRST-TIME PIN CHECK (ONLY WHEN DASHBOARD IS UNLOCKED)
  // -------------------------------------------------------
  useEffect(() => {
    async function checkPin() {
      // Only check after teacher unlocks dashboard
      if (!isDashboardUnlocked()) return;

      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) return;

      const { data: teacher, error } = await supabase
        .from("teachers")
        .select("pin_code")
        .eq("id", user.id)
        .single();

      if (error) {
        console.error("Error loading teacher:", error);
        return;
      }

      // First-time user → send to PIN change screen
      if (teacher?.pin_code === "0000") {
        console.log("First login → PIN = 0000 → redirecting to /change-pin");
        navigate("/change-pin", { replace: true });
      }
    }

    checkPin();
  }, [navigate]);


  useEffect(() => {
  return () => {
    setShowAddStudent(false);
  };
}, []);

  // ---------- LOADERS ----------
  const loadStudents = useCallback(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from("students")
      .select("*")
      .eq("teacher_id", user.id)
      .order("student_name", { ascending: true });

    setStudents(data || []);
  }, []);

  const loadGroups = useCallback(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from("groups")
      .select("*")
      .eq("teacher_id", user.id)
      .order("created_at", { ascending: true });

    setGroups(data || []);
  }, []);

  const loadTaskAssignmentsForList = useCallback(async (taskRows) => {
    if (!taskRows || taskRows.length === 0) {
      setTaskAssignmentsByTaskId({});
      return;
    }

    const taskIds = taskRows.map((task) => task.id);
    const { data, error } = await supabase
      .from("task_assignments")
      .select("task_id, group_id")
      .in("task_id", taskIds);

    if (error) {
      console.error("Error loading task assignments:", error);
      return;
    }

    const map = {};
    (data || []).forEach((row) => {
      if (!row.group_id) return;
      if (!map[row.task_id]) map[row.task_id] = [];
      map[row.task_id].push(row.group_id);
    });

    setTaskAssignmentsByTaskId(map);
  }, []);

  const loadTasks = useCallback(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from("tasks")
      .select("*")
      .eq("teacher_id", user.id)
      .order("created_at", { ascending: true });

    const taskRows = data || [];
    setTasks(taskRows);
    loadTaskAssignmentsForList(taskRows);
  }, [loadTaskAssignmentsForList]);

  useEffect(() => {
    loadStudents();
    loadGroups();
    loadTasks();
  }, [loadGroups, loadStudents, loadTasks]);

  const loadTaskProgress = useCallback(async () => {
    if (!tasks.length) {
      setTaskProgressByTaskId({});
      return;
    }

    const activeStudents = students.filter((student) => !student.hidden_from_class);
    const studentIds = activeStudents.map((student) => student.id);
    const activeStudentIds = new Set(studentIds);
    const taskIds = tasks.map((task) => task.id);
    const targetedTaskIds = tasks
      .filter((task) => task.audience === "targeted")
      .map((task) => task.id);

    let assignmentRows = [];
    if (targetedTaskIds.length > 0) {
      const { data, error } = await supabase
        .from("task_assignments")
        .select("task_id, group_id, student_id")
        .in("task_id", targetedTaskIds);

      if (error) {
        console.error("Error loading task progress assignments:", error);
        return;
      }

      assignmentRows = data || [];
    }

    const groupIds = [...new Set(
      assignmentRows.map((row) => row.group_id).filter(Boolean)
    )];

    let groupStudentRows = [];
    if (groupIds.length > 0) {
      const { data, error } = await supabase
        .from("group_students")
        .select("group_id, student_id")
        .in("group_id", groupIds);

      if (error) {
        console.error("Error loading task progress group members:", error);
        return;
      }

      groupStudentRows = data || [];
    }

    let statusRows = [];
    if (studentIds.length > 0 && taskIds.length > 0) {
      const { data, error } = await supabase
        .from("task_status")
        .select("task_id, student_id, completed")
        .in("task_id", taskIds)
        .in("student_id", studentIds);

      if (error) {
        console.error("Error loading task progress statuses:", error);
        return;
      }

      statusRows = data || [];
    }

    const groupStudentsByGroupId = {};
    groupStudentRows.forEach((row) => {
      if (!row.group_id || !row.student_id) return;
      if (!groupStudentsByGroupId[row.group_id]) {
        groupStudentsByGroupId[row.group_id] = new Set();
      }
      groupStudentsByGroupId[row.group_id].add(row.student_id);
    });

    const assignedStudentsByTaskId = {};
    tasks.forEach((task) => {
      if (task.audience !== "targeted") {
        assignedStudentsByTaskId[task.id] = new Set(studentIds);
        return;
      }

      const assigned = new Set();
      assignmentRows
        .filter((row) => row.task_id === task.id)
        .forEach((row) => {
          if (row.student_id && activeStudentIds.has(row.student_id)) assigned.add(row.student_id);
          if (row.group_id && groupStudentsByGroupId[row.group_id]) {
            groupStudentsByGroupId[row.group_id].forEach((studentId) => {
              if (activeStudentIds.has(studentId)) assigned.add(studentId);
            });
          }
        });
      assignedStudentsByTaskId[task.id] = assigned;
    });

    const completedStudentsByTaskId = {};
    statusRows.forEach((row) => {
      if (!row.completed || !row.task_id || !row.student_id) return;
      const assigned = assignedStudentsByTaskId[row.task_id];
      if (!assigned || !assigned.has(row.student_id)) return;
      if (!completedStudentsByTaskId[row.task_id]) {
        completedStudentsByTaskId[row.task_id] = new Set();
      }
      completedStudentsByTaskId[row.task_id].add(row.student_id);
    });

    const progressMap = {};
    tasks.forEach((task) => {
      const assignedCount = assignedStudentsByTaskId[task.id]?.size || 0;
      const completedCount = completedStudentsByTaskId[task.id]?.size || 0;
      progressMap[task.id] = {
        assigned: assignedCount,
        completed: completedCount,
      };
    });

    setTaskProgressByTaskId(progressMap);
  }, [students, tasks]);

  useEffect(() => {
    loadTaskProgress();
  }, [loadTaskProgress]);

  const loadTaskAssignments = useCallback(async (taskId) => {
    const { data, error } = await supabase
      .from("task_assignments")
      .select("group_id")
      .eq("task_id", taskId);

    if (error) {
      console.error("Error loading task assignments:", error);
      return;
    }

    const groupIds = [];

    (data || []).forEach((row) => {
      if (row.group_id) groupIds.push(row.group_id);
    });

    setEditTaskGroupIds(groupIds);
  }, []);

  useEffect(() => {
    if (!editTask) {
      setEditTaskAudience("all");
      setEditTaskGroupIds([]);
      return;
    }

    setEditTaskAudience(editTask.audience || "all");
    setEditTaskGroupIds([]);
    loadTaskAssignments(editTask.id);
  }, [editTask, loadTaskAssignments]);


  // Update Tasks function
async function updateTask(
  taskId,
  newTitle,
  newIcon,
  newPriority,
  newAudience,
  groupIds,
  samenwerken,
  zelfNakijken,
  feedbackEnabled
) {
  const hasTargets = groupIds.length > 0;
  const audience =
    newAudience === "targeted" && hasTargets ? "targeted" : "all";

  const { error } = await supabase
    .from("tasks")
    .update({
      title: newTitle,
      icon: newIcon,
      priority: newPriority,
      audience,
      samenwerken,
      zelf_nakijken: zelfNakijken,
      feedback_enabled: feedbackEnabled,
    })
    .eq("id", taskId);

  if (error) {
    console.error("Error updating task:", error);
    return;
  }

  const { error: deleteError } = await supabase
    .from("task_assignments")
    .delete()
    .eq("task_id", taskId);

  if (deleteError) {
    console.error("Error clearing task assignments:", deleteError);
    return;
  }

  if (audience === "targeted") {
    const assignments = [
      ...groupIds.map((groupId) => ({
        task_id: taskId,
        group_id: groupId,
      })),
    ];

    if (assignments.length > 0) {
      const { error: assignmentError } = await supabase
        .from("task_assignments")
        .insert(assignments);

      if (assignmentError) {
        console.error("Error updating task assignments:", assignmentError);
      }
    }
  }

  setEditTask(null);
  loadTasks();
}

// Modifying title of tasklist
async function saveTaskListTitle(newTitle) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { error } = await supabase
    .from("teachers")
    .update({ task_list_title: newTitle })
    .eq("id", user.id);

  if (error) {
    console.error("Error updating task list title:", error);
    alert("Kon de titel niet opslaan.");
    return;
  }

  setTaskListTitle(newTitle);
}

async function saveClassTimer(enabled, minutes, restart = enabled) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const duration = Math.min(180, Math.max(1, Number.parseInt(minutes, 10) || 10));
  const startedAt = enabled && restart ? new Date().toISOString() : null;
  const { error } = await supabase
    .from("teachers")
    .update({
      class_timer_enabled: enabled,
      class_timer_duration_minutes: duration,
      class_timer_started_at: startedAt,
      class_timer_paused_at: null,
    })
    .eq("id", user.id);

  if (error) {
    console.error("Error updating class timer:", error);
    alert("Kon de timerinstellingen niet opslaan.");
    return;
  }

  setClassTimerEnabled(enabled);
  setClassTimerMinutes(duration);
  setClassTimerStartedAt(startedAt);
  setClassTimerPausedAt(null);
}

async function pauseClassTimer() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user || !classTimerStartedAt || classTimerPausedAt) return;

  const pausedAt = new Date().toISOString();
  const { error } = await supabase
    .from("teachers")
    .update({ class_timer_paused_at: pausedAt })
    .eq("id", user.id);

  if (error) {
    console.error("Error pausing class timer:", error);
    alert("Kon de timer niet pauzeren.");
    return;
  }
  setClassTimerPausedAt(pausedAt);
}

async function resumeClassTimer() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user || !classTimerStartedAt || !classTimerPausedAt) return;

  const pausedDuration = Math.max(0, Date.now() - Date.parse(classTimerPausedAt));
  const resumedStart = new Date(Date.parse(classTimerStartedAt) + pausedDuration).toISOString();
  const { error } = await supabase
    .from("teachers")
    .update({ class_timer_started_at: resumedStart, class_timer_paused_at: null })
    .eq("id", user.id);

  if (error) {
    console.error("Error resuming class timer:", error);
    alert("Kon de timer niet hervatten.");
    return;
  }
  setClassTimerStartedAt(resumedStart);
  setClassTimerPausedAt(null);
}

async function toggleTasksVisibleOnHome(checked) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { error } = await supabase
    .from("teachers")
    .update({ tasks_visible_on_home: checked })
    .eq("id", user.id);

  if (error) {
    console.error("Error updating tasks visibility:", error);
    alert("Kon zichtbaarheid niet opslaan.");
    return;
  }
  setTasksVisibleOnHome(checked);
}


  // ---------- STUDENT ACTIONS ----------
async function addStudent(name) {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  await supabase.from("students").insert([
    { teacher_id: user.id, student_name: name }
  ]);

  loadStudents();
}



  async function deleteStudent() {
    await supabase.from("students").delete().eq("id", deleteStudentId);
    setDeleteStudentId(null);
    loadStudents();
  }

  async function setStudentHidden(studentId, hidden) {
    const { error } = await supabase
      .from("students")
      .update({ hidden_from_class: hidden })
      .eq("id", studentId);

    if (error) {
      console.error("Could not update student visibility:", error);
      return;
    }

    loadStudents();
  }

  // ---------- TASK ACTIONS ----------
  async function addTask() {
    if (!newTaskTitle.trim()) return;

    const {
      data: { user },
    } = await supabase.auth.getUser();

    const hasTargets = newTaskGroupIds.length > 0;
    const audience =
      newTaskAudience === "targeted" && hasTargets ? "targeted" : "all";

    const { data: createdTask, error } = await supabase
      .from("tasks")
      .insert([
        {
          title: newTaskTitle,
          icon: newTaskIcon,
          priority: newTaskPriority,
          samenwerken: newTaskSamenwerken,
          zelf_nakijken: newTaskZelfNakijken,
          feedback_enabled: newTaskFeedbackEnabled,
          audience,
          teacher_id: user.id,
        },
      ])
      .select("id")
      .single();

    if (error) {
      console.error(error);
      return;
    }

    if (audience === "targeted" && createdTask?.id) {
      const assignments = [
        ...newTaskGroupIds.map((groupId) => ({
          task_id: createdTask.id,
          group_id: groupId,
        })),
      ];

      if (assignments.length > 0) {
        const { error: assignmentError } = await supabase
          .from("task_assignments")
          .insert(assignments);

        if (assignmentError) {
          console.error("Error creating task assignments:", assignmentError);
        }
      }
    }

    setNewTaskTitle("");     // clear field
    setNewTaskIcon("📘");    // reset icon
    setNewTaskPriority("required");
    setNewTaskSamenwerken(false);
    setNewTaskZelfNakijken(false);
    setNewTaskFeedbackEnabled(false);
    setNewTaskAudience("all");
    setNewTaskGroupIds([]);
    loadTasks();             // keep modal open
    // DON'T CLOSE THE MODAL HERE

  }

  async function deleteTask() {
    await supabase.from("tasks").delete().eq("id", deleteTaskId);
    setDeleteTaskId(null);
    loadTasks();
  }

  async function deleteGroup() {
    await supabase.from("groups").delete().eq("id", deleteGroupId);
    setDeleteGroupId(null);
    loadGroups();
  }

  // ---------- UI ----------
  const groupNameById = new Map(groups.map((group) => [group.id, group.name]));

  function formatTaskProgress(taskId) {
    const progress = taskProgressByTaskId[taskId] || { completed: 0, assigned: 0 };
    return `${progress.completed}/${progress.assigned}`;
  }

  return (
    <div className="ct-page ct-dashboard p-6">

     <div className="ct-dashboard-header">
  <div>
    <p className="ct-eyebrow">Werkruimte</p>
    <h1 className="ct-page-title">Leerkracht dashboard</h1>
  </div>

  <div className="flex gap-3">
    <button
      onClick={() => navigate("/change-pin")}
      className="ct-button-secondary"
    >
      PIN wijzigen
    </button>

    <button
      onClick={() => navigate("/classroom")}
      className="ct-button-secondary"
    >
      ← Terug naar klasoverzicht
    </button>
  </div>
</div>


      {/* TWO-COLUMN LAYOUT */}
      <div className="ct-dashboard-grid">

        {/* LEFT COLUMN = TASKS (2/3 width) */}
        <div className="ct-surface ct-dashboard-tasks p-6">
          
<div className="ct-task-list-toolbar">
  <div className="ct-task-list-heading">
    <h2 className="text-xl font-semibold">{taskListTitle}</h2>
    <div className="ct-task-settings-menu-wrap">
      <button
        type="button"
        className="ct-overflow-button"
        title="Instellingen takenlijst"
        aria-label="Instellingen takenlijst"
        onClick={() => setShowTaskSettings(true)}
      >
        ⋯
      </button>
    </div>
  </div>
  <button
    onClick={() => setShowAddTask(true)}
    className="ct-button-primary"
  >
    + Nieuwe taken
  </button>
</div>

          {tasks.length === 0 ? (
            <p className="text-gray-500 italic">Nog geen taken toegevoegd.</p>
          ) : (
          <div className="space-y-5">
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Moetjes
              </h3>
              <ul className="space-y-2">
                {tasks
                  .filter((task) => task.priority !== "optional")
                  .map((task) => (
                    <li
                      key={task.id}
                      className="ct-dashboard-task-row"
                      onClick={() => {
                        console.log("EDIT CLICKED", task);
                        setEditTask(task);
                      }}
                    >
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="ct-task-icon" aria-hidden="true"><TaskIcon value={task.icon} className="ct-task-svg" /></span>
                        <span className="text-gray-800">{task.title}</span>
                        {task.audience === "targeted" &&
                          (taskAssignmentsByTaskId[task.id] || [])
                            .map((groupId) => ({
                              id: groupId,
                              name: groupNameById.get(groupId),
                            }))
                            .filter((group) => group.name)
                            .map((group) => (
                              <span
                                key={group.id}
                                className="text-xs bg-gray-200 px-2 py-1 rounded-full"
                              >
                                {group.name}
                              </span>
                            ))}
                      </div>

                      <div className="flex items-center gap-3">
                        {task.samenwerken && (
                          <span className="text-lg" title="Samenwerken">
                            🤝
                          </span>
                        )}
                        {task.zelf_nakijken && (
                          <span className="text-lg" title="Zelf nakijken">
                            🔑
                          </span>
                        )}
                        {task.feedback_enabled && (
                          <span className="text-lg" title="Feedback gevraagd">
                            💬
                          </span>
                        )}
                        <span className="text-xs font-semibold text-gray-500 min-w-12 text-right">
                          {formatTaskProgress(task.id)}
                        </span>
                        {task.feedback_enabled && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFeedbackReportTask(task);
                            }}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Feedback report
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // prevent edit modal opening
                            setDeleteTaskId(task.id);
                          }}
                          className="ct-overflow-button"
                          title="Taak verwijderen"
                          aria-label="Taak verwijderen"
                        >
                          ⋯
                        </button>
                      </div>
                    </li>
                  ))}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Magjes
              </h3>
              <ul className="space-y-2">
                {tasks
                  .filter((task) => task.priority === "optional")
                  .map((task) => (
                    <li
                      key={task.id}
                      className="ct-dashboard-task-row"
                      onClick={() => {
                        console.log("EDIT CLICKED", task);
                        setEditTask(task);
                      }}
                    >
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="ct-task-icon" aria-hidden="true"><TaskIcon value={task.icon} className="ct-task-svg" /></span>
                        <span className="text-gray-800">{task.title}</span>
                        {task.audience === "targeted" &&
                          (taskAssignmentsByTaskId[task.id] || [])
                            .map((groupId) => ({
                              id: groupId,
                              name: groupNameById.get(groupId),
                            }))
                            .filter((group) => group.name)
                            .map((group) => (
                              <span
                                key={group.id}
                                className="text-xs bg-gray-200 px-2 py-1 rounded-full"
                              >
                                {group.name}
                              </span>
                            ))}
                      </div>

                      <div className="flex items-center gap-3">
                        {task.samenwerken && (
                          <span className="text-lg" title="Samenwerken">
                            🤝
                          </span>
                        )}
                        {task.zelf_nakijken && (
                          <span className="text-lg" title="Zelf nakijken">
                            🔑
                          </span>
                        )}
                        {task.feedback_enabled && (
                          <span className="text-lg" title="Feedback gevraagd">
                            💬
                          </span>
                        )}
                        <span className="text-xs font-semibold text-gray-500 min-w-12 text-right">
                          {formatTaskProgress(task.id)}
                        </span>
                        {task.feedback_enabled && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFeedbackReportTask(task);
                            }}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Feedback report
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // prevent edit modal opening
                            setDeleteTaskId(task.id);
                          }}
                          className="ct-overflow-button"
                          title="Taak verwijderen"
                          aria-label="Taak verwijderen"
                        >
                          ⋯
                        </button>
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          </div>

          )}
        </div>

        {/* RIGHT COLUMN = STUDENTS (1/3 width) */}
        <div className="ct-surface ct-dashboard-sidebar p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold">Leerlingen</h2>
            <button
              onClick={() => setShowAddStudent(true)}
              className="ct-button-primary text-sm"
            >
              + Nieuwe leerlingen
            </button>
          </div>

          <StudentList
            students={students}
            onDelete={(id) => setDeleteStudentId(id)}
            onHide={(id) => setStudentHidden(id, true)}
            onShow={(id) => setStudentHidden(id, false)}
          />

          <div className="mt-6 border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">Groepen</h3>
              <button
                type="button"
                onClick={() => setShowAddGroup(true)}
                className="ct-button-secondary text-sm"
              >
                + Nieuwe groep
              </button>
            </div>

            {groups.length === 0 ? (
              <p className="text-gray-500 italic">Nog geen groepen.</p>
            ) : (
              <ul className="space-y-2">
                {groups.map((group) => (
                  <li
                    key={group.id}
                    className="ct-group-row"
                    onClick={() => setEditGroup(group)}
                  >
                    <span className="font-semibold">{group.name}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteGroupId(group.id);
                      }}
                      className="ct-overflow-button"
                      title="Groep verwijderen"
                      aria-label="Groep verwijderen"
                    >
                      ⋯
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* STUDENT MODALS */}
{showAddStudent && (
  <AddStudentModal
    onAdd={(name) => {
      addStudent(name);
    }}
    onClose={() => setShowAddStudent(false)}
  />
)}



      {deleteStudentId && (
        <DeleteStudentModal
          studentName={students.find((student) => student.id === deleteStudentId)?.student_name}
          onConfirm={deleteStudent}
          onClose={() => setDeleteStudentId(null)}
        />
      )}

      {deleteGroupId && (
        <DeleteGroupModal
          onConfirm={deleteGroup}
          onClose={() => setDeleteGroupId(null)}
        />
      )}

      {showAddGroup && (
        <AddGroupModal
          students={students}
          onClose={() => setShowAddGroup(false)}
          onSaved={() => {
            setShowAddGroup(false);
            loadGroups();
          }}
        />
      )}

      {editGroup && (
        <EditGroupModal
          group={editGroup}
          students={students}
          onClose={() => setEditGroup(null)}
          onSaved={() => {
            setEditGroup(null);
            loadGroups();
          }}
        />
      )}

      {/* TASK MODALS */}
      {showTaskSettings && (
        <TaskListSettingsModal
          currentTitle={taskListTitle}
          tasksVisibleOnHome={tasksVisibleOnHome}
          timerEnabled={classTimerEnabled}
          timerPaused={Boolean(classTimerPausedAt)}
          timerMinutes={classTimerMinutes}
          timerStartedAt={classTimerStartedAt}
          timerPausedAt={classTimerPausedAt}
          onSaveTitle={saveTaskListTitle}
          onToggleTasks={toggleTasksVisibleOnHome}
          onToggleTimer={(enabled, minutes) => saveClassTimer(enabled, minutes, enabled)}
          onPauseTimer={pauseClassTimer}
          onResumeTimer={resumeClassTimer}
          onResetTimer={(minutes) => saveClassTimer(true, minutes, true)}
          onResetProgress={() => setShowResetConfirm(true)}
          onClose={() => setShowTaskSettings(false)}
        />
      )}

{showResetConfirm && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-sm">
      <h2 className="text-xl font-bold mb-4">
        Alle voortgang terugzetten?
      </h2>

      <p className="text-gray-600 mb-6">
        Dit verwijdert alle afgevinkte taken voor elke leerling. 
        Weet je het zeker?
      </p>

      <div className="flex justify-end gap-3">
        <button
          onClick={() => setShowResetConfirm(false)}
          className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
        >
          Annuleren
        </button>

        <button
          onClick={async () => {
            await resetAllTaskStatuses();
            setShowResetConfirm(false);
          }}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Reset
        </button>
      </div>
    </div>
  </div>
)}


      {showAddTask && (
        <AddTaskModal
          title={newTaskTitle}
          setTitle={setNewTaskTitle}
          selectedIcon={newTaskIcon}
          setSelectedIcon={setNewTaskIcon}
          priority={newTaskPriority}
          setPriority={setNewTaskPriority}
          samenwerken={newTaskSamenwerken}
          setSamenwerken={setNewTaskSamenwerken}
          zelfNakijken={newTaskZelfNakijken}
          setZelfNakijken={setNewTaskZelfNakijken}
          feedbackEnabled={newTaskFeedbackEnabled}
          setFeedbackEnabled={setNewTaskFeedbackEnabled}
          audience={newTaskAudience}
          setAudience={setNewTaskAudience}
          groups={groups}
          selectedGroupIds={newTaskGroupIds}
          setSelectedGroupIds={setNewTaskGroupIds}
          onAdd={addTask}
          onClose={() => setShowAddTask(false)}
        />
      )}

      {editTask && (
        <EditTaskModal
          task={editTask}
          audience={editTaskAudience}
          setAudience={setEditTaskAudience}
          groups={groups}
          selectedGroupIds={editTaskGroupIds}
          setSelectedGroupIds={setEditTaskGroupIds}
          onClose={() => setEditTask(null)}
          onSave={(
            title,
            icon,
            priority,
            audience,
            groupIds,
            samenwerken,
            zelfNakijken,
            feedbackEnabled
          ) =>
            updateTask(
              editTask.id,
              title,
              icon,
              priority,
              audience,
              groupIds,
              samenwerken,
              zelfNakijken,
              feedbackEnabled
            )
          }
        />
      )}


      {feedbackReportTask && (
        <TaskFeedbackReportModal
          task={feedbackReportTask}
          students={students}
          onClose={() => setFeedbackReportTask(null)}
        />
      )}

      {deleteTaskId && (
        <DeleteTaskModal
          onConfirm={deleteTask}
          onClose={() => setDeleteTaskId(null)}
        />
      )}
    </div>
  );
}
